import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { LoadingController, AlertController, NavController, Platform } from '@ionic/angular';
import { VehicleSummaryModel } from 'app/models/dms/vehicle.model';
import { VehicleSearchParamsModel } from 'app/models/dms/vehicleSearchParams.model';
import { VehicleSearchResultModel } from 'app/models/dms/vehicleSearchResult.model';
import { CustomFieldResultModel } from 'app/models/shared/customField.model';
import { ListItemModel } from 'app/models/shared/listItem.model';
import { GeneralService } from 'app/services/general.service';
import { MessageService } from 'app/services/message.service';
import { StorageService } from 'app/services/storage.service';
import { UserService } from 'app/services/user.service';
import { VehicleService } from 'app/services/vehicle.service';
import { Constants } from 'app/utility/constants';
import { VehicleOrderBy, EntityType, BulkUpdateVehicleOrderBy } from 'app/utility/enums';
import { StringHelper } from 'app/utility/stringhelper';
import { BehaviorSubject, Subscription, finalize, timeout, Observable, map, firstValueFrom } from 'rxjs';

const searchParamsCacheKey: string = 'search-params';
const bulkUpdateVehiclesCacheKey: string = 'bulk-update-vehicles';
const bulkUpdateVehiclesCacheTtlMs: number = 60 * 60 * 1000;

const VEHICLE_STATUS_ALL: string = 'All';

@Component({
    selector: 'app-bulk-update-vehicle-list',
    templateUrl: './bulk-update-vehicle-list.component.html',
    styleUrls: ['./bulk-update-vehicle-list.component.scss'],
    standalone: false
})
export class BulkUpdateVehicleListComponent implements OnInit, OnChanges, OnDestroy
{

    @Input() searchParams: VehicleSearchParamsModel;
    @Input() allowActions: boolean = true;
    @Input() actionIconMode: 'popover' | 'emit' = 'popover';
    @Input() addNew: boolean = false;
    @Input() modal: boolean = false;
    @Input() excludeVehicleIds: string[] = [];
    @Input() selectedVehicleIds: string[] = [];
    @Input() selectionEnabled: boolean = true;
    @Output() vehicleSelected = new EventEmitter<VehicleSummaryModel>();
    @Output() actionSelected = new EventEmitter<any>();
    @Output() actionsClicked = new EventEmitter<{ vehicle: VehicleSummaryModel; event: any }>();
    @Output() vehicleIdsChanged = new EventEmitter<string[]>();
    @Output() selectedCountChanged = new EventEmitter<number>();
    @Output() newVehicleSelected = new EventEmitter<string>();
    @Output() resultCountChanged = new EventEmitter<number>();
    private searchParamsSubject: BehaviorSubject<VehicleSearchParamsModel>;

    hasUserSearch: boolean = false;

    showSpinner: boolean = false;

    isLoadingNewSearch: boolean = false;
    searchReloading: boolean = false;
    searchLoading: boolean = false;

    private resetVehicles: boolean = true;
    vehicles: VehicleSummaryModel[];

    private allVehicles: VehicleSummaryModel[] = [];
    private filteredVehicles: VehicleSummaryModel[] = [];
    private readonly initialFetchPageSize: number = 10000;
    private readonly renderBatchSize: number = 20;
    private renderedVehicleCount: number = 20;

    private totalRowCnt: number = 0;

    private selectedVehicleIdSet: Set<string> = new Set<string>();
    private userTouchedSelection: boolean = false;

    rowCnt: number;
    rowCntFormatted: string = "0";

    sortColumns: ListItemModel[];

    currentSortColumn: string;

    isMobile: boolean = false;

    selectedVehicle: VehicleSummaryModel;
    statusFilterActive: boolean = false;

    customFields: CustomFieldResultModel[] | undefined = undefined;
    showCustomFields: boolean = false;

    constructor(
        private vehicleService: VehicleService,
        private userService: UserService,
        private generalService: GeneralService,
        private storage: StorageService,
        public loadingController: LoadingController,
        private alertController: AlertController,
        private navCtrl: NavController,
        private platform: Platform,
        private messageService: MessageService
    )
    {
        this.searchLoading = true;
    }

    @Input() inParams: VehicleSearchParamsModel;

    ngOnInit()
    {
        this.platform.ready().then(() =>
        {
            this.isMobile = (this.platform.width() < 800);
            this.platform.resize.subscribe(() =>
            {
                this.isMobile = (this.platform.width() < 800);
            });
        });

        if (this.inParams == null)
        {
            this.inParams = new VehicleSearchParamsModel();
        }

        if (this.inParams.orderByIn == null)
        {
            this.inParams.orderByIn = VehicleOrderBy.StockNo;
            this.inParams.orderByDesc = true;
        }

        this.currentSortColumn = this.inParams.orderByIn;

        if (!this.inParams.enforce)
        {
            this.storage.get(Constants.IonicStorage_VehicleListSort).then((val) =>
            {
                if (val)
                {
                    this.currentSortColumn = val;
                    this.inParams.orderByIn = val;
                    this.inParams.orderByDesc = false;
                    if (val.includes("Desc"))
                    {
                        this.inParams.orderByIn = val.replace("Desc", "");
                        this.inParams.orderByDesc = true;
                    }
                }
            });
        }

        this.sortColumns = [
            new ListItemModel(BulkUpdateVehicleOrderBy.StockNo + '', 'Stock no.', 'Low to High', 'asc'),
            new ListItemModel(BulkUpdateVehicleOrderBy.StockNoDesc + '', 'Stock no.', 'High to Low', 'desc'),
            new ListItemModel(BulkUpdateVehicleOrderBy.MakeModel + '', 'Make/Model', 'A to Z', 'asc'),
            new ListItemModel(BulkUpdateVehicleOrderBy.MakeModelDesc + '', 'Make/Model', 'Z to A', 'desc'),
            new ListItemModel(BulkUpdateVehicleOrderBy.BodyStyle + '', 'Body style', 'A to Z', 'asc'),
            new ListItemModel(BulkUpdateVehicleOrderBy.BodyStyleDesc + '', 'Body style', 'Z to A', 'desc'),
            new ListItemModel(BulkUpdateVehicleOrderBy.Year + '', 'Year', 'Low to High', 'asc'),
            new ListItemModel(BulkUpdateVehicleOrderBy.YearDesc + '', 'Year', 'High to Low', 'desc'),
            new ListItemModel(BulkUpdateVehicleOrderBy.RetailPrice + '', 'Price', 'Low to High', 'asc'),
            new ListItemModel(BulkUpdateVehicleOrderBy.RetailPriceDesc + '', 'Price', 'High to Low', 'desc'),
            new ListItemModel(BulkUpdateVehicleOrderBy.CreatedDateDesc + '', 'Date added', 'New to Old', 'desc'),
            new ListItemModel(BulkUpdateVehicleOrderBy.CreatedDate + '', 'Date added', 'Old to New', 'asc'),
            new ListItemModel(BulkUpdateVehicleOrderBy.ModifiedDateDesc + '', 'Date modified', 'New to Old', 'desc'),
            new ListItemModel(BulkUpdateVehicleOrderBy.ModifiedDate + '', 'Date modified', 'Old to New', 'asc'),
            new ListItemModel(BulkUpdateVehicleOrderBy.OpportCountDesc + '', 'Opportunity count', 'High to Low', 'desc'),
            new ListItemModel(BulkUpdateVehicleOrderBy.OpportCount + '', 'Opportunity count', 'Low to High', 'asc'),
        ];

        this.initSearch();

        const vehicleMessageSub = this.messageService.vehicleMessage.subscribe(
            (message) =>
            {
                console.log("dms vehicle message received ", message);

                for (let i = 0; i < this.vehicles.length; i++)
                {
                    if (this.vehicles[i].vehicleId == message.vehicleId)
                    {
                        this.vehicles[i] = message;
                        // console.log(this.vehicles[i], "updated");
                    }
                }
            }
        );
        this.subscriptions.push(vehicleMessageSub);
    }

    ngOnChanges(changes: SimpleChanges)
    {
        if (changes['selectedVehicleIds'])
        {
            this.setSelectionFromInput(this.selectedVehicleIds);
            this.emitSelectedCountChanged();
        }

        if (changes['excludeVehicleIds'] && !changes['excludeVehicleIds'].firstChange)
        {
            this.pruneSelectionForExclusions();
            this.emitSelectedVehicleIds();
            this.emitSelectedCountChanged();
        }
    }

    subscriptions: Subscription[] = [];

    ngOnDestroy()
    {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }

    private initSearch(): void
    {
        this.resetVehicles = true;
        this.showSpinner = true;

        this.setSelectionFromInput(this.selectedVehicleIds);

        // Default: nothing selected.
        // Selection will be driven purely by `selectedVehicleIds` input and user interaction.

        this.inParams.pageNumber = 1;
        this.inParams.pageSize = 10000;
        // Status locked to unsold (20); UI may show filter but should be disabled.
        this.inParams.vehicleSoldStateIn = '20';
        this.inParams.orderByIn = this.inParams.orderByIn.replace("Desc", "");


        if (this.userService.mcMinVersion("3.2.20"))
        {
            this.customFields = [];
            this.generalService.getCachedCustomFields(EntityType.Vehicle)
                .pipe( 
                    finalize(() =>
                    {
                        this.loadData(); // Ensures this runs after the observable completes or errors 
                    })
                )
                .subscribe({
                    next: (x) =>
                    {
                        this.customFields = x.filter(y => y.showInGrid);
                    },
                    error: (err) =>
                    {
                        console.error('Error fetching custom fields:', err);
                    }
                });
        }
        else
        {
            this.loadData();
        }

    }

    searchAllStatuses()
    {
        this.inParams.vehicleSoldStateIn = VEHICLE_STATUS_ALL;
        this.loadData();
    }


    async loadData(event = null)
    {
        // Status locked to unsold (20); we keep this flag true so the UI can show the "search all statuses" prompt if needed.
        this.inParams.vehicleSoldStateIn = '20';
        this.statusFilterActive = true;

        const fetchParams = this.buildInitialFetchParams();

        try
        {
            // Attempt to load from cache first to provide instant results; if cache is invalid or missing, fetch from API and update cache.
            // We only want to cache for 1 hour as vehicles can change frequently and we want to balance freshness with performance.
            let vehicles: VehicleSummaryModel[] = [];
            const cached = await this.storage.get(bulkUpdateVehiclesCacheKey) as | { vehicles: VehicleSummaryModel[]; expiry: number } | null;
            const isCacheValid = !!cached && Array.isArray(cached.vehicles) && typeof cached.expiry === 'number' && cached.expiry > Date.now();
            if (isCacheValid)
            {
                vehicles = cached.vehicles;
            }
            else
            {
                if (cached)
                {
                    await this.storage.remove(bulkUpdateVehiclesCacheKey);
                }

                vehicles = (await firstValueFrom(this.vehicleService.getVehicles(fetchParams))).vehicles ?? [];

                await this.storage.set(bulkUpdateVehiclesCacheKey, {vehicles, expiry: Date.now() + bulkUpdateVehiclesCacheTtlMs});
            }

            vehicles = vehicles ?? [];

            if (vehicles.length > 0)
            {
                this.vehicleService.populateVehicleThumbnailUrls(vehicles);
            }

            this.allVehicles = vehicles;
            this.totalRowCnt = this.getTotalVehicleCount();
            this.resultCountChanged.emit(this.totalRowCnt);

            this.pruneSelectionNotInAllVehicles();
            this.emitSelectedVehicleIds();
            this.emitSelectedCountChanged();

            this.applyLocalFilters(true);

            if (event)
            {
                event.target.complete();
                event.target.disabled = !(this.hasMore());
            }
        }
        catch (err)
        {
            console.error(err);
            this.userService.handleError(err);
        }
        finally
        {
            this.showSpinner = false;
            this.isLoadingNewSearch = false;
            this.searchLoading = false;
            this.searchReloading = false;
        }
    }

    private getTotalVehicleCount(): number
    {
        const excluded = new Set(this.excludeVehicleIds ?? []);
        return (this.allVehicles ?? []).filter(v => !!v?.vehicleId && !excluded.has(v.vehicleId)).length;
    }

    private buildInitialFetchParams(): VehicleSearchParamsModel
    {
        const p = new VehicleSearchParamsModel();

        // Fetch once: unsold vehicles across all dealerships. Filtering/search/sort happens locally.
        p.pageNumber = 1;
        p.pageSize = this.initialFetchPageSize;
        p.vehicleSoldStateIn = '20';
        p.dealership = Constants.EmptyGuid;
        p.searchText = '';

        // Deterministic default sort (local sort will control the UI ordering).
        p.orderByIn = this.inParams?.orderByIn ?? BulkUpdateVehicleOrderBy.StockNo;
        p.orderByDesc = this.inParams?.orderByDesc ?? true;

        return p;
    }

    public refreshFromParams(resetVisible: boolean = true)
    {
        this.applyLocalFilters(resetVisible);
    }

    public applyLocalFilters(resetVisible: boolean = true)
    {
        if (!this.allVehicles)
        {
            this.allVehicles = [];
        }

        const q = (this.inParams?.searchText ?? '').toString().trim().toLowerCase();
        const dealershipId = this.inParams?.dealership;
        const hasDealershipFilter = !!dealershipId && dealershipId !== Constants.EmptyGuid;

        const excluded = new Set(this.excludeVehicleIds ?? []);

        let result = this.allVehicles.filter(v => !!v && !excluded.has(v.vehicleId));

        if (hasDealershipFilter)
        {
            result = result.filter(v => v.dealershipId === dealershipId);
        }

        if (q)
        {
            result = result.filter(v =>
            {
                const haystack = [
                    v.stockNo,
                    v.regNo,
                    v.yearManuModelCaption,
                    v.manuModelCaption,
                    v.bodyStyleCaption,
                    v.vin,
                    v.chassisNo,
                    v.companyCode,
                ].filter(Boolean).join(' ').toLowerCase();
                return haystack.includes(q);
            });
        }

        result = this.sortVehicles(result);

        this.filteredVehicles = result;
        this.rowCnt = this.filteredVehicles.length;
        this.rowCntFormatted = this.numberWithCommas(this.rowCnt);

        if (resetVisible)
        {
            this.renderedVehicleCount = this.renderBatchSize;
        }

        // Selection is independent from filtering; keep it stable across search/sort/filter.
        this.pruneSelectionForExclusions();
        this.emitSelectedCountChanged();

        this.vehicles = this.filteredVehicles.slice(0, this.renderedVehicleCount);
    }

    public isVehicleSelected(vehicleId: string): boolean
    {
        return !!vehicleId && this.selectedVehicleIdSet.has(vehicleId);
    }

    public checkSelection(): 'All' | 'Partial' | 'None'
    {
        const total = this.filteredVehicles?.length ?? 0;
        if (total === 0)
        {
            return 'None';
        }

        let selectedInFilter = 0;
        for (const v of this.filteredVehicles)
        {
            if (v?.vehicleId && this.selectedVehicleIdSet.has(v.vehicleId))
            {
                selectedInFilter++;
            }
        }

        if (selectedInFilter === 0) return 'None';
        if (selectedInFilter === total) return 'All';
        return 'Partial';
    }

    public toggleSelectAll(ev?: any): void
    {
        if (!this.selectionEnabled) return;
        if (ev?.stopPropagation)
        {
            ev.stopPropagation();
        }

        this.userTouchedSelection = true;

        const state = this.checkSelection();
        if (state === 'All')
        {
            for (const v of this.filteredVehicles ?? [])
            {
                if (v?.vehicleId)
                {
                    this.selectedVehicleIdSet.delete(v.vehicleId);
                }
            }
        }
        else
        {
            for (const v of this.filteredVehicles ?? [])
            {
                if (v?.vehicleId)
                {
                    this.selectedVehicleIdSet.add(v.vehicleId);
                }
            }
        }

        this.emitSelectedVehicleIds();
        this.emitSelectedCountChanged();
    }

    public toggleVehicleSelection(vehicle: VehicleSummaryModel, ev?: any): void
    {
        if (!this.selectionEnabled) return;
        if (ev?.stopPropagation)
        {
            ev.stopPropagation();
        }

        const id = vehicle?.vehicleId;
        if (!id) return;

        this.userTouchedSelection = true;

        if (this.selectedVehicleIdSet.has(id))
        {
            this.selectedVehicleIdSet.delete(id);
        }
        else
        {
            this.selectedVehicleIdSet.add(id);
        }

        this.emitSelectedVehicleIds();
        this.emitSelectedCountChanged();
    }

    private sortVehicles(list: VehicleSummaryModel[]): VehicleSummaryModel[]
    {
        if (!list) return [];

        const orderBy = (this.inParams?.orderByIn ?? BulkUpdateVehicleOrderBy.StockNo).toString();
        const desc = !!this.inParams?.orderByDesc;

        const getString = (s: any) => (s ?? '').toString().toLowerCase();
        const getNumber = (n: any) => (typeof n === 'number' ? n : (parseFloat(n) || 0));
        const getDate = (d: any) => (d ? new Date(d).getTime() : 0);

        const value = (v: VehicleSummaryModel) =>
        {
            switch (orderBy)
            {
                case BulkUpdateVehicleOrderBy.StockNo:
                    return getNumber(v.stockNo);
                case BulkUpdateVehicleOrderBy.MakeModel:
                    return getString(v.manuModelCaption || v.yearManuModelCaption);
                case BulkUpdateVehicleOrderBy.Year:
                    return getNumber(v.manuYear);
                case BulkUpdateVehicleOrderBy.RetailPrice:
                    return getNumber((v.retailPriceAmount ?? v.effectivePriceAmount) ?? 0);
                case BulkUpdateVehicleOrderBy.CreatedDate:
                    return getDate(v.createdDate);
                case BulkUpdateVehicleOrderBy.ModifiedDate:
                    return getDate(v.modifiedDate);
                case BulkUpdateVehicleOrderBy.OpportCount:
                    return getNumber(v.opportunityCount);
                case BulkUpdateVehicleOrderBy.BodyStyle:
                    return getString(v.bodyStyleCaption);
                default:
                    return getString(v.stockNo);
            }
        };

        const sorted = [...list].sort((a, b) =>
        {
            const av: any = value(a);
            const bv: any = value(b);
            if (av < bv) return -1;
            if (av > bv) return 1;
            return 0;
        });

        return desc ? sorted.reverse() : sorted;
    }


    async loadSpinner()
    {
        const loading = await this.loadingController.create({
            mode: "ios",
            message: 'Loading...',
            translucent: true,
            cssClass: 'custom-class custom-loading',
            duration: 60000
        });
        return await loading.present();
    }

    public hasMore(): boolean
    {
        return (this.filteredVehicles?.length ?? 0) > (this.vehicles?.length ?? 0);
    }

    public loadMore(event = null): void
    {
        this.renderedVehicleCount += this.renderBatchSize;
        this.vehicles = this.filteredVehicles.slice(0, this.renderedVehicleCount);

        if (event)
        {
            event.target.complete();
            event.target.disabled = !(this.hasMore());
        }
    }

    sortChanged(ev: any)
    {
        this.isLoadingNewSearch = true;
        this.searchReloading = true;

        this.inParams.orderByIn = ev.replace("Desc", "");

        if (!this.inParams.enforce)
        {
            this.storage.set(Constants.IonicStorage_BulkUpdateVehicleListSort, ev);
        }

        if (ev.includes("Desc"))
        {
            this.inParams.orderByDesc = true;
        }
        else
        {
            this.inParams.orderByDesc = false;
        }

        this.applyLocalFilters(true);

        this.isLoadingNewSearch = false;
        this.searchReloading = false;
    }

    private pruneSelectionForExclusions(): void
    {
        const excluded = new Set(this.excludeVehicleIds ?? []);
        if (!excluded.size) return;

        for (const id of excluded)
        {
            this.selectedVehicleIdSet.delete(id);
        }
    }

    private pruneSelectionNotInAllVehicles(): void
    {
        const excluded = new Set(this.excludeVehicleIds ?? []);
        const allowed = new Set((this.allVehicles ?? []).map(v => v?.vehicleId).filter(Boolean) as string[]);

        for (const id of Array.from(this.selectedVehicleIdSet))
        {
            if (!allowed.has(id) || excluded.has(id))
            {
                this.selectedVehicleIdSet.delete(id);
            }
        }
    }

    private setSelectionFromInput(ids: string[] | null | undefined): void
    {
        const next = new Set((ids ?? []).filter(Boolean));
        if (this.areSetsEqual(next, this.selectedVehicleIdSet))
        {
            return;
        }

        this.selectedVehicleIdSet = next;
    }

    private areSetsEqual(a: Set<string>, b: Set<string>): boolean
    {
        if (a.size !== b.size) return false;
        for (const v of a)
        {
            if (!b.has(v)) return false;
        }
        return true;
    }

    private emitSelectedVehicleIds(): void
    {
        this.vehicleIdsChanged.emit(Array.from(this.selectedVehicleIdSet));
    }

    private emitSelectedCountChanged(): void
    {
        // Selected count should persist regardless of local search/filter/sort.
        this.selectedCountChanged.emit(this.selectedVehicleIdSet.size);
    }

    public selectItem(vehicle: VehicleSummaryModel)
    {
        // console.log("selectItem", vehicle);
        this.selectedVehicle = vehicle;
        this.vehicleSelected.emit(vehicle);
    }

    public actionSelect(action: any)
    {
        if (action.action == 'deleted')
        {
            this.vehicles = this.vehicles.filter(v => v.vehicleId != action.vehicle.vehicleId);
        }
        else
        {
            this.actionSelected.emit(action);
        }
    }

    public actionIconClicked(payload: { vehicle: VehicleSummaryModel; event: any })
    {
        this.actionsClicked.emit(payload);
    }


    public handleError(error: any): void
    {
        this.userService.handleError(error);
    }


    async presentAlert(title: string, message: string, backText: string = "", okText: string = "OK", next: boolean = false, nextText: string = "Continue")
    {
        var buttons = [];
        if (backText)
        {
            buttons.push({
                text: backText,
                role: 'confirmback',
                cssClass: 'secondary',
                handler: () =>
                {
                    this.navCtrl.navigateBack("/vehicles");
                }
            });
        }

        buttons.push({
            text: okText,
            role: 'cancel',
            cssClass: 'secondary',
            handler: () =>
            {
                this.searchParamsSubject.next(this.inParams);
            }
        });

        // if (next)
        // {
        // 	buttons.push({
        // 		text: nextText,
        // 		role: 'test',
        // 		cssClass: 'secondary',
        // 		handler: () =>
        // 		{
        // 		}
        // 	});
        // }

        const alert = await this.alertController.create({
            header: title,
            message: message,
            buttons: buttons
        });

        await alert.present();
    }

    public numberWithCommas(x: any): string
    {
        return StringHelper.numberWithCommas(x);
    }
}
